import { PmiGroupSpectrumBindPayload, PmiSpectrumBindPayload } from '../services/api';

export const DEFAULT_PMI_SPECTRUM_WS_URL = 'wss://api.yanclex.com/spectrum/ws';

interface LocationLike {
  host: string;
  protocol: string;
}

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || value instanceof Blob || value instanceof ArrayBuffer) {
    return null;
  }

  return value as Record<string, unknown>;
}

function parseWsRecord(data: unknown): Record<string, unknown> | null {
  if (typeof data === 'string') {
    try {
      return toRecord(JSON.parse(data));
    } catch {
      return null;
    }
  }

  return toRecord(data);
}

function readText(record: Record<string, unknown> | null, keys: string[], fallback = '') {
  if (!record) {
    return fallback;
  }

  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return fallback;
}

function readNumber(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
  }

  return null;
}

function normalizeLegacyElements(value: unknown): Record<string, number | string> {
  const record = toRecord(value);
  if (!record) {
    return {};
  }

  return Object.keys(record).reduce<Record<string, number | string>>((next, key) => {
    const value = record[key];
    if (typeof value === 'number' || typeof value === 'string') {
      next[key] = value;
    }
    return next;
  }, {});
}

function normalizeXrfElements(value: unknown): Record<string, number | string> {
  if (!Array.isArray(value)) {
    return {};
  }

  return value.reduce<Record<string, number | string>>((next, item) => {
    const element = toRecord(item);
    const symbol = readText(element, ['symbol', 'name']);
    if (!symbol) {
      return next;
    }

    const concentration = element?.concentration;
    const numeric = readNumber(concentration);
    if (numeric != null) {
      next[symbol] = numeric;
    } else if (typeof concentration === 'string' && concentration.trim()) {
      next[symbol] = concentration.trim();
    }
    return next;
  }, {});
}

function hasElements(elements: Record<string, number | string>) {
  return Object.keys(elements).length > 0;
}

function getBasename(path: string) {
  const pieces = path.split('/');
  return pieces[pieces.length - 1] || path;
}

function getXrfFileName(record: Record<string, unknown>) {
  const name = readText(record, ['name']);
  if (name) {
    return name.toLowerCase().endsWith('.xs') ? name : `${name}.xs`;
  }

  const path = readText(record, ['xsPath']);
  return path ? getBasename(path) : 'spectrum.xs';
}

function getMeasuredAt(record: Record<string, unknown>, preview: Record<string, unknown> | null) {
  const measuredAt = readText(preview, ['dateMeasured', 'measuredAt', 'measured_at']);
  if (measuredAt) {
    return measuredAt;
  }

  const receivedAt = readNumber(record.receivedAt);
  if (receivedAt != null) {
    return new Date(receivedAt).toISOString();
  }

  return new Date().toISOString();
}

function buildLegacyPayload(
  record: Record<string, unknown>,
  inspectionSerial: string,
  fallbackGrade: string,
): PmiSpectrumBindPayload | null {
  const elements = normalizeLegacyElements(record.elements);
  if (!hasElements(elements)) {
    return null;
  }

  return {
    elements,
    file_name: readText(record, ['file_name', 'fileName', 'filename'], 'spectrum.json'),
    grade: readText(record, ['grade', 'material_grade', 'steel_grade'], fallbackGrade),
    inspection_serial: readText(record, ['inspection_serial', 'inspectionSerial'], inspectionSerial),
    instrument: readText(record, ['instrument', 'device', 'machine'], ''),
    measured_at: readText(record, ['measured_at', 'measuredAt'], new Date().toISOString()),
    raw_status: readText(record, ['raw_status', 'rawStatus', 'status'], ''),
    sample_name: readText(record, ['sample_name', 'sampleName', 'sample'], ''),
  };
}

function buildXrfPayload(
  record: Record<string, unknown>,
  inspectionSerial: string,
  fallbackGrade: string,
): PmiSpectrumBindPayload | null {
  const parseError = readText(record, ['parseError']);
  if (parseError) {
    return null;
  }

  const preview = toRecord(record.preview);
  const elements = normalizeXrfElements(preview?.elements);
  if (!hasElements(elements)) {
    return null;
  }

  return {
    elements,
    file_name: getXrfFileName(record),
    grade: fallbackGrade,
    inspection_serial: inspectionSerial,
    instrument: readText(preview, ['instrument'], readText(record, ['deviceId'])),
    measured_at: getMeasuredAt(record, preview),
    raw_status: preview?.hasResults === false ? 'NoResults' : 'Ok',
    sample_name: readText(preview, ['sampleName'], readText(record, ['name'])),
  };
}

export function buildPmiSpectrumPayloadFromWsData(
  data: unknown,
  inspectionSerial: string,
  fallbackGrade: string,
): PmiSpectrumBindPayload | null {
  const record = parseWsRecord(data);
  if (!record) {
    return null;
  }

  if (record.type === 'spectrum') {
    return buildXrfPayload(record, inspectionSerial, fallbackGrade);
  }

  return buildLegacyPayload(record, inspectionSerial, fallbackGrade);
}

export function buildPmiGroupSpectrumPayloadFromWsData(
  data: unknown,
  pmigCode: string,
  fallbackGrade: string,
): PmiGroupSpectrumBindPayload | null {
  const basePayload = buildPmiSpectrumPayloadFromWsData(data, pmigCode, fallbackGrade);
  if (!basePayload) {
    return null;
  }

  const elements = Object.keys(basePayload.elements).reduce<Record<string, number>>((result, key) => {
    const numeric = Number(basePayload.elements[key]);
    if (Number.isFinite(numeric)) {
      result[key] = numeric;
    }
    return result;
  }, {});
  if (!Object.keys(elements).length) {
    return null;
  }

  return {
    elements,
    file_name: basePayload.file_name,
    grade: basePayload.grade,
    instrument: basePayload.instrument,
    measured_at: basePayload.measured_at || new Date().toISOString(),
    method: 'XRF',
    pmig_code: pmigCode,
    raw_status: basePayload.raw_status,
    reason: 'PDA 临时组标光谱数据绑定',
    sample_name: basePayload.sample_name || pmigCode,
  };
}

function normalizeKeyValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizeKeyValue);
  }

  const record = toRecord(value);
  if (record) {
    return Object.keys(record).sort().reduce<Record<string, unknown>>((next, key) => {
      next[key] = normalizeKeyValue(record[key]);
      return next;
    }, {});
  }

  return value;
}

export function buildPmiSpectrumPayloadKey(payload: Pick<
PmiSpectrumBindPayload,
'elements' | 'instrument' | 'measured_at' | 'raw_status' | 'sample_name'
>) {
  return JSON.stringify({
    elements: normalizeKeyValue(payload.elements),
    instrument: payload.instrument,
    measured_at: payload.measured_at,
    raw_status: payload.raw_status,
    sample_name: payload.sample_name,
  });
}

function normalizeEpochMs(value: number) {
  return value > 0 && value < 100000000000 ? value * 1000 : value;
}

export function isPmiSpectrumWsDataFresh(data: unknown, minReceivedAt: number) {
  if (!minReceivedAt) {
    return true;
  }

  const record = parseWsRecord(data);
  if (!record || record.type !== 'spectrum') {
    return true;
  }

  const receivedAt = readNumber(record.receivedAt);
  if (receivedAt == null) {
    return true;
  }

  return normalizeEpochMs(receivedAt) >= minReceivedAt;
}

export function isPmiGroupSpectrumWsDataFresh(data: unknown, minReceivedAt: number) {
  if (!minReceivedAt) {
    return true;
  }
  const record = parseWsRecord(data);
  if (!record) {
    return false;
  }
  if (record.type === 'spectrum') {
    const receivedAt = readNumber(record.receivedAt);
    return receivedAt != null && normalizeEpochMs(receivedAt) >= minReceivedAt;
  }

  const measuredAt = readText(record, ['measured_at', 'measuredAt']);
  const measuredAtMs = measuredAt ? Date.parse(measuredAt) : Number.NaN;
  return Number.isFinite(measuredAtMs) && measuredAtMs >= minReceivedAt;
}

function buildSameOriginPmiSpectrumWsUrl(locationLike: LocationLike) {
  const protocol = locationLike.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${locationLike.host}/spectrum/ws`;
}

function isLoopbackHostname(hostname: string) {
  const normalized = hostname.toLowerCase().replace(/^\[|\]$/g, '');
  return normalized === 'localhost'
    || normalized === '127.0.0.1'
    || normalized === '::1'
    || normalized === '0.0.0.0';
}

function readExplicitWsHostname(url: string) {
  try {
    return new URL(url).hostname;
  } catch {
    return '';
  }
}

function readLocationHostname(locationLike: LocationLike) {
  try {
    return new URL(`${locationLike.protocol}//${locationLike.host}`).hostname;
  } catch {
    return locationLike.host.split(':')[0] || '';
  }
}

export function buildPmiSpectrumWsUrl(options: {
  deviceId?: string;
  explicitUrl?: string;
  location?: LocationLike;
} = {}) {
  const explicitUrl = options.explicitUrl?.trim();
  const locationLike = options.location || window.location;
  const explicitHostname = explicitUrl ? readExplicitWsHostname(explicitUrl) : '';
  const locationHostname = readLocationHostname(locationLike);
  const shouldUseExplicitUrl = explicitUrl
    && (!isLoopbackHostname(explicitHostname) || isLoopbackHostname(locationHostname));
  let baseUrl = buildSameOriginPmiSpectrumWsUrl(locationLike);
  if (explicitUrl) {
    baseUrl = shouldUseExplicitUrl ? explicitUrl : DEFAULT_PMI_SPECTRUM_WS_URL;
  }
  const deviceId = options.deviceId?.trim();

  if (!deviceId) {
    return baseUrl;
  }

  const joiner = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${joiner}device=${encodeURIComponent(deviceId)}`;
}
